import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, UserPlus, X, Search, Check, UserMinus } from 'lucide-react';
import { API } from '../context/AdminAuthContext';

/**
 * FloorAssignPicker — modal for a Floor In-charge to hand out a
 * warehouse task (picking / loading / put-away) to another staff
 * member straight from the Floor page.
 *
 * Only mounted when `me.allow_floor_task_assignment === true` (super
 * admin flips this on for trusted leads via /admin/users). Backend
 * enforcement lives in `PATCH /admin/warehouse/tasks/{id}` which
 * already accepts a floor_incharge caller — the toggle is a UI
 * enabler for one-handed reassignment from the phone.
 *
 * Design constraints (matching FloorTasks.jsx one-handed reach):
 *   * Full-screen sheet on phones; centered card on tablet+.
 *   * 48px+ tap rows for each candidate.
 *   * Search box at the top for warehouses with dozens of staff.
 *   * "Unassign" row at the top for revoking without a replacement.
 */
const FloorAssignPicker = ({ open, task, authHeaders, onClose, onAssigned }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`${API}/admin/users`, { headers: authHeaders })
      .then((r) => {
        if (cancelled) return;
        // Filter to active users. Users with warehouse role AND
        // whose role matches the task type get sorted to the top;
        // everyone else stays below (a manager can still reassign
        // to a name that doesn't strictly match the role).
        const users = (r.data || []).filter((u) => u.active !== false);
        const scored = users
          .map((u) => ({ ...u, _score: _relevanceScore(u, task.type) }))
          .sort((a, b) => b._score - a._score || (a.name || a.email).localeCompare(b.name || b.email));
        setCandidates(scored);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load staff.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, task?.type, authHeaders]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter((u) =>
      (u.name || '').toLowerCase().includes(needle)
      || (u.email || '').toLowerCase().includes(needle),
    );
  }, [q, candidates]);

  if (!open || !task) return null;

  const assign = async (email) => {
    setSaving(email || 'unassign');
    setError('');
    try {
      const r = await axios.patch(
        `${API}/admin/warehouse/tasks/${task.id}`,
        { assignee_email: email || '' },
        { headers: authHeaders },
      );
      onAssigned?.(r.data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to assign.');
    } finally {
      setSaving('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
        data-testid="floor-assign-picker"
      >
        {/* Header */}
        <div className="p-5 bg-slate-950 text-white flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-400 flex items-center justify-center flex-shrink-0">
            <UserPlus className="w-5 h-5 text-blue-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
              Assign {task.type}
            </div>
            <div className="text-lg font-bold text-white leading-tight truncate">
              {task.product_name || task.product_id || `Task ${task.id.slice(0, 8)}`}
            </div>
            {task.order_number && (
              <div className="text-xs text-slate-400 mt-0.5">Order #{task.order_number}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            aria-label="Close"
            data-testid="floor-assign-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search + current assignee */}
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
              data-testid="floor-assign-search"
            />
          </div>
          {(task.assignee_name || task.assignee_email) && (
            <button
              type="button"
              onClick={() => assign('')}
              disabled={!!saving}
              className="mt-3 w-full h-11 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="floor-assign-unassign"
            >
              {saving === 'unassign' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <UserMinus className="w-4 h-4" />
              )}
              Unassign {task.assignee_name || task.assignee_email}
            </button>
          )}
        </div>

        {/* Candidate list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-400">
              <Loader2 className="w-6 h-6 mx-auto animate-spin" />
              <div className="mt-2 text-sm">Loading staff…</div>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-4">{error}</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              {q ? 'No staff matches that search.' : 'No active staff found. Ask the admin to create some in Admin › Users.'}
            </div>
          ) : (
            filtered.map((u) => {
              const isCurrent = (task.assignee_email || '').toLowerCase() === (u.email || '').toLowerCase();
              const roleLabel = _bestRoleLabel(u, task.type);
              return (
                <button
                  key={u.email}
                  type="button"
                  onClick={() => !isCurrent && assign(u.email)}
                  disabled={!!saving || isCurrent}
                  className={`w-full min-h-[64px] px-4 py-3 flex items-center gap-3 border-b border-slate-100 text-left hover:bg-blue-50 active:bg-blue-100 disabled:opacity-70 ${isCurrent ? 'bg-emerald-50/60' : ''}`}
                  data-testid={`floor-assign-candidate-${u.email}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${isCurrent ? 'bg-emerald-500 text-white' : 'bg-blue-100 text-blue-700'}`}>
                    {(u.name || u.email || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 text-sm truncate">
                      {u.name || u.email}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    {roleLabel && (
                      <div className="mt-0.5 inline-flex items-center gap-1">
                        <span className="text-[9px] uppercase tracking-wider font-bold bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">
                          {roleLabel}
                        </span>
                      </div>
                    )}
                  </div>
                  {saving === u.email ? (
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                  ) : isCurrent ? (
                    <Check className="w-5 h-5 text-emerald-600" />
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};


// Higher score = more relevant candidate for the task type. We surface
// staff whose warehouse role matches the task first (picker for picking
// tasks, floor_incharge/manager for loading + put-away).
function _relevanceScore(user, taskType) {
  const roles = new Set(_userWhRoles(user));
  if (taskType === 'picking') {
    if (roles.has('picker')) return 3;
    if (roles.has('floor_incharge') || roles.has('manager')) return 2;
  } else if (taskType === 'loading' || taskType === 'putaway') {
    if (roles.has('floor_incharge') || roles.has('manager')) return 3;
    if (roles.has('picker')) return 1;
  }
  if (roles.has('viewer')) return 0;
  // Non-warehouse admins still qualify (a dispatch lead can be
  // assigned) — just ranked lowest.
  return roles.size ? 1 : 0;
}

function _bestRoleLabel(user, taskType) {
  const roles = _userWhRoles(user);
  if (!roles.length) return '';
  const priority = taskType === 'picking'
    ? ['picker', 'floor_incharge', 'manager', 'viewer']
    : ['floor_incharge', 'manager', 'picker', 'viewer'];
  for (const p of priority) {
    if (roles.includes(p)) return p.replace('_', ' ');
  }
  return roles[0];
}

function _userWhRoles(user) {
  if (Array.isArray(user?.warehouse_roles) && user.warehouse_roles.length) return user.warehouse_roles;
  if (user?.warehouse_role) return [user.warehouse_role];
  return [];
}


export default FloorAssignPicker;
